'use client';
import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, TrendingDown } from 'lucide-react';

type SortKey = 'revenue' | 'units' | 'returnRate' | 'growth';
type SortDir = 'asc' | 'desc';

const products = [
  {
    id: 'prod-001',
    name: 'NexusCore Enterprise',
    category: 'SaaS Platform',
    revenue: 1842300,
    units: 412,
    returnRate: 1.2,
    growth: 18.4,
    status: 'active' as const,
    margin: 72.3,
  },
  {
    id: 'prod-002',
    name: 'DataSync Pro',
    category: 'Integration Tool',
    revenue: 924100,
    units: 1840,
    returnRate: 2.8,
    growth: 24.1,
    status: 'active' as const,
    margin: 68.7,
  },
  {
    id: 'prod-003',
    name: 'AnalyticsEdge',
    category: 'BI Module',
    revenue: 687400,
    units: 3210,
    returnRate: 0.9,
    growth: 11.2,
    status: 'active' as const,
    margin: 81.4,
  },
  {
    id: 'prod-004',
    name: 'FlowDesk Starter',
    category: 'Helpdesk',
    revenue: 341800,
    units: 5640,
    returnRate: 4.1,
    growth: -6.3,
    status: 'review' as const,
    margin: 54.2,
  },
  {
    id: 'prod-005',
    name: 'SecureVault API',
    category: 'Security',
    revenue: 218700,
    units: 890,
    returnRate: 0.4,
    growth: 34.8,
    status: 'active' as const,
    margin: 88.1,
  },
  {
    id: 'prod-006',
    name: 'MobileConnect SDK',
    category: 'SDK',
    revenue: 172840,
    units: 2140,
    returnRate: 3.6,
    growth: -12.1,
    status: 'declining' as const,
    margin: 61.8,
  },
  {
    id: 'prod-007',
    name: 'ReportBuilder Plus',
    category: 'Reporting',
    revenue: 100200,
    units: 1820,
    returnRate: 2.1,
    growth: 7.4,
    status: 'active' as const,
    margin: 74.9,
  },
];

const statusConfig = {
  active: { label: 'Active', class: 'bg-positive/10 text-positive' },
  review: { label: 'Under Review', class: 'bg-warning/10 text-warning' },
  declining: { label: 'Declining', class: 'bg-negative/10 text-negative' },
};

export default function ProductPerformanceTable() {
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...products].sort((a, b) => {
    const mult = sortDir === 'asc' ? 1 : -1;
    return (a[sortKey] - b[sortKey]) * mult;
  });

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={13} className="text-muted-foreground" />;
    return sortDir === 'asc'
      ? <ArrowUp size={13} className="text-primary" />
      : <ArrowDown size={13} className="text-primary" />;
  };

  return (
    <div className="card-surface">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">Product Performance</h2>
          <p className="text-sm text-muted-foreground">Ranked by revenue — May 2026</p>
        </div>
        <span className="text-xs text-muted-foreground bg-muted rounded px-2 py-1">
          {products.length} products
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 label-caps text-muted-foreground">#</th>
              <th className="text-left py-3 px-4 label-caps text-muted-foreground">Product</th>
              <th className="text-right py-3 px-4 label-caps text-muted-foreground">
                <button
                  onClick={() => handleSort('revenue')}
                  className="flex items-center gap-1 ml-auto hover:text-foreground transition-colors"
                >
                  Revenue <SortIcon col="revenue" />
                </button>
              </th>
              <th className="text-right py-3 px-4 label-caps text-muted-foreground">
                <button
                  onClick={() => handleSort('units')}
                  className="flex items-center gap-1 ml-auto hover:text-foreground transition-colors"
                >
                  Units <SortIcon col="units" />
                </button>
              </th>
              <th className="text-right py-3 px-4 label-caps text-muted-foreground">
                <button
                  onClick={() => handleSort('returnRate')}
                  className="flex items-center gap-1 ml-auto hover:text-foreground transition-colors"
                >
                  Return Rate <SortIcon col="returnRate" />
                </button>
              </th>
              <th className="text-right py-3 px-4 label-caps text-muted-foreground">
                <button
                  onClick={() => handleSort('growth')}
                  className="flex items-center gap-1 ml-auto hover:text-foreground transition-colors"
                >
                  Growth <SortIcon col="growth" />
                </button>
              </th>
              <th className="text-left py-3 px-4 label-caps text-muted-foreground">Status</th>
              <th className="text-right py-3 px-4 label-caps text-muted-foreground">Margin</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((product, i) => {
              const status = statusConfig[product.status];
              return (
                <tr
                  key={product.id}
                  className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors group"
                >
                  <td className="py-3 px-4 text-sm text-muted-foreground tabular-nums">
                    {i + 1}
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-foreground">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{product.category}</p>
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-semibold text-foreground tabular-nums">
                    ${product.revenue.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-foreground tabular-nums">
                    {product.units.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`text-sm font-medium tabular-nums ${product.returnRate > 3 ? 'text-negative' : 'text-foreground'}`}>
                      {product.returnRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`flex items-center justify-end gap-1 text-sm font-semibold tabular-nums ${
                      product.growth >= 0 ? 'text-positive' : 'text-negative'
                    }`}>
                      {product.growth >= 0
                        ? <TrendingUp size={13} />
                        : <TrendingDown size={13} />}
                      {product.growth >= 0 ? '+' : ''}{product.growth}%
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`label-caps rounded-full px-2.5 py-1 ${status.class}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-muted-foreground tabular-nums">
                    {product.margin}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}